import { createSlice } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { notify } from 'components/Common/Toast';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import * as groupsApi from 'services/groupsApi';
import { AnnouncementGroup, AnnouncementGroupDetailState, RemoveGroupMember } from 'types/announcements';
import type { AnnouncementGroupMemberRow } from 'types/announcementsV1';

const initialState: AnnouncementGroupDetailState = {
    members: [],
    details: null,
    membersLoading: false,
    detailsLoading: false,
    error: null,
    groupName: null,
    groupImage: null,
    inflightDetailsParishId: null,
    inflightDetailsGroupId: null,
    inflightMembersParishId: null,
    inflightMembersGroupId: null,
    membersTotal: 0,
};

export const fetchAnnouncementGroupDetail = (parishId: string, id: string) => (dispatch: any) => {
    dispatch(fetchAnnouncementGroupInformation({ parishId, id }));
    dispatch(fetchAnnouncementGroupMembers({ parishId, id }));
};

export const fetchAnnouncementGroupInformation = createAsyncThunkWrapper<
    { details: AnnouncementGroup; parishId: string; groupId: string } | null,
    { parishId: string; id: string }
>(
    'groupDetails/fetchAnnouncementGroupDetails',
    async ({ parishId, id }, { getState }) => {
        if (!parishId || !id) return null;
        try {
            const details = await groupsApi.getGroup(parishId, id);
            if (parishSelectionMoved(getState, parishId)) return null;
            return { details, parishId, groupId: id };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load group' }
);

export const fetchAnnouncementGroupMembers = createAsyncThunkWrapper<
    { members: AnnouncementGroupMemberRow[]; total: number; parishId: string; groupId: string } | null,
    { parishId: string; id: string }
>(
    'groupDetails/fetchAnnouncementGroupMembers',
    async ({ parishId, id }, { getState }) => {
        if (!parishId || !id) return null;
        try {
            const res = await groupsApi.listGroupMembers(parishId, id, { page: 1, pageSize: 200 }); // intentional hard cap for group roster UI
            if (parishSelectionMoved(getState, parishId)) return null;
            return { members: res.items, total: res.total, parishId, groupId: id };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load group members' }
);

export const addMemberToGroup = createAsyncThunkWrapper<
    { ok: true; parishId: string } | null,
    { parishId: string; groupId: string; userId: string }
>(
    'groupDetails/addMemberToGroup',
    async ({ parishId, groupId, userId }, { dispatch, getState }) => {
        if (!parishId || !groupId || !userId) return null;
        try {
            await groupsApi.putGroupMember(parishId, groupId, userId);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Member added successfully.', 'success');
            dispatch(fetchAnnouncementGroupDetail(parishId, groupId));
            return { ok: true as const, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to add member' }
);

export const removeMemberFromGroup = createAsyncThunkWrapper<{ ok: true; parishId: string } | null, RemoveGroupMember>(
    'groupDetails/removeMemberFromGroup',
    async ({ parishId, groupId, userId }, { dispatch, getState }) => {
        if (!parishId || !groupId || !userId) return null;
        try {
            await groupsApi.removeGroupMember(parishId, groupId, userId);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Member Removed Successfully', 'success');
            dispatch(fetchAnnouncementGroupDetail(parishId, groupId));
            return { ok: true as const, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to remove member' }
);

const announcementGroupDetailSlice = createSlice({
    name: 'groupDetailsSlice',
    initialState,
    reducers: {
        clearAnnouncementGroupDetail(state) {
            state.members = [];
            state.details = null;
            state.inflightDetailsParishId = null;
            state.inflightDetailsGroupId = null;
            state.inflightMembersParishId = null;
            state.inflightMembersGroupId = null;
            state.membersTotal = 0;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchAnnouncementGroupInformation.pending, (state, action) => {
            state.detailsLoading = true;
            state.details = null;
            state.inflightDetailsParishId = action.meta.arg.parishId;
            state.inflightDetailsGroupId = action.meta.arg.id;
        });
        builder.addCase(fetchAnnouncementGroupInformation.fulfilled, (state, action) => {
            if (!action.payload) {
                if (state.inflightDetailsParishId === action.meta.arg.parishId && state.inflightDetailsGroupId === action.meta.arg.id) {
                    state.detailsLoading = false;
                }
                return;
            }
            const { details, parishId, groupId } = action.payload;
            if (parishId !== state.inflightDetailsParishId || groupId !== state.inflightDetailsGroupId) {
                return;
            }
            state.detailsLoading = false;
            const totalParticipants =
                state.inflightMembersGroupId === groupId && !state.membersLoading ? state.membersTotal : details.totalParticipants;
            state.details = {
                ...details,
                totalParticipants,
            };
        });
        builder.addCase(fetchAnnouncementGroupInformation.rejected, (state, action) => {
            if (state.inflightDetailsParishId === action.meta.arg.parishId && state.inflightDetailsGroupId === action.meta.arg.id) {
                state.detailsLoading = false;
            }
        });
        builder.addCase(fetchAnnouncementGroupMembers.pending, (state, action) => {
            state.membersLoading = true;
            state.members = [];
            state.membersTotal = 0;
            state.inflightMembersParishId = action.meta.arg.parishId;
            state.inflightMembersGroupId = action.meta.arg.id;
        });
        builder.addCase(fetchAnnouncementGroupMembers.fulfilled, (state, action) => {
            if (!action.payload) {
                if (state.inflightMembersParishId === action.meta.arg.parishId && state.inflightMembersGroupId === action.meta.arg.id) {
                    state.membersLoading = false;
                }
                return;
            }
            const { members, total, parishId, groupId } = action.payload;
            if (parishId !== state.inflightMembersParishId || groupId !== state.inflightMembersGroupId) {
                return;
            }
            state.membersLoading = false;
            state.members = members;
            state.membersTotal = total;
            // Only merge participant count onto details for the same group.
            if (state.details && state.inflightDetailsGroupId === groupId && state.details.id === groupId) {
                state.details = {
                    ...state.details,
                    totalParticipants: total,
                };
            }
        });
        builder.addCase(fetchAnnouncementGroupMembers.rejected, (state, action) => {
            if (state.inflightMembersParishId === action.meta.arg.parishId && state.inflightMembersGroupId === action.meta.arg.id) {
                state.membersLoading = false;
            }
        });
    },
});

export const { clearAnnouncementGroupDetail } = announcementGroupDetailSlice.actions;
export default announcementGroupDetailSlice.reducer;
